import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../hooks/useSidebar';
import ModuleCard from './components/ModuleCard';
import SubscriptionSummary from './components/SubscriptionSummary';
import ActiveModulesList from './components/ActiveModulesList';
import LocationFilter from './components/LocationFilter';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';

const ModuleSelectionDashboard = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [userRole] = useState('Safety Manager');

  const modules = [
    {
      id: 'safety-control-tower',
      name: 'Safety Control Tower',
      description: 'Leadership command centre consolidating all critical safety signals — KPIs with sparklines, behavioural and process safety trends, environmental and medical indicators, compliance tracking, AI insights and action hub.',
      icon: 'Tower',
      status: 'active',
      isPrimary: true,
      locations: 4,
      lastUpdated: '01/21/2025',
      route: '/safety-control-tower'
    },
    {
      id: 'work-permit',
      name: 'Work Permit System',
      description: 'Comprehensive permit lifecycle management system for tracking work permits, extensions, and approvals across all locations with real-time status monitoring.',
      icon: 'FileText',
      status: 'active',
      isPrimary: true,
      locations: 4,
      lastUpdated: '12/01/2025',
      route: '/work-permit-tracking'
    },
    {
      id: 'safety-analytics',
      name: 'Safety Analytics Dashboard',
      description: 'Advanced analytics and reporting module providing insights into safety metrics, hazard trends, and compliance data with visual charts and export capabilities.',
      icon: 'BarChart3',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/02/2025',
      route: '/safety-analytics-dashboard'
    },
    {
      id: 'incident-management',
      name: 'Incident Management',
      description: 'Track and manage workplace incidents, near-misses, and safety observations with investigation workflows, recommendation tracking, and compliance reporting.',
      icon: 'AlertTriangle',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/04/2025',
      route: '/incident-investigation-tracker'
    },
    {
      id: 'risk-identification',
      name: 'Risk Identification',
      description: 'HAZID worksheet, hazard mapping, deviation analysis, and AI-assisted hazard detection',
      icon: 'AlertCircle',
      status: 'active',
      color: 'purple',
      features: ['HAZID Worksheet', 'Hazard Mapping', 'Deviation ID', 'What-If Checklist', 'AI Suggestions'],
      metrics: {
        total: 324,
        label: 'Hazards Identified'
      },
      route: '/risk-identification'
    },
    {
      id: 'psi-repository',
      name: 'PSI Repository',
      description: 'Process safety information and chemical data management',
      icon: 'Database',
      status: 'active',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      path: '/process-safety-information-repository',
      submodules: [
        { name: 'Chemical Information', path: '/chemical-information-manager' },
        { name: 'Compatibility Matrix', path: '/chemical-compatibility-matrix-manager' },
        { name: 'Database Integration', path: '/material-database-integration-hub' },
      ],
    },
    {
      id: 'safety-audits',
      name: 'Safety Audits',
      description: 'Create safety audit surveys from question banks, publish to users, track completion, and analyze responses with historical comparisons and site ratings.',
      icon: 'ClipboardCheck',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/06/2025',
      route: '/safety-audit-dashboard'
    },
    {
      id: 'unsafe-acts-conditions',
      name: 'Unsafe Acts & Conditions',
      description: 'Comprehensive observation system for unsafe acts and conditions with analytics, entry forms, task management, and AI vision integration.',
      icon: 'Eye',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/06/2025',
      route: '/unsafe-acts-conditions-analytics-dashboard'
    },
    {
      id: 'training-compliance',
      name: 'Training & Compliance',
      description: 'Comprehensive training program management with analytics for mandatory and skill-based training, personnel coverage tracking, assessment metrics, materials repository, and certification management.',
      icon: 'GraduationCap',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/06/2025',
      route: '/training-compliance-dashboard'
    },
    {
      id: 'statutory-compliance',
      name: 'Statutory Compliance',
      description: 'Complete statutory compliance management with analytics dashboard, task scheduling, document repository, timeline tracking, and data validation to ensure regulatory requirements are met.',
      icon: 'Scale',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/06/2025',
      route: '/statutory-compliance-analytics-dashboard'
    },
    {
      id: 'reports',
      name: 'Reports Management',
      description: 'Comprehensive report generation system with automated scheduling, validation checks, custom report builder, and AI-powered text generation for executive summaries and compliance narratives.',
      icon: 'FileBarChart',
      status: 'active',
      isPrimary: false,
      locations: 4,
      lastUpdated: '12/06/2025',
      route: '/reports-management-dashboard'
    },
    {
      id: 'equipment-inspection',
      name: 'Equipment Inspection',
      description: 'Schedule and track equipment safety inspections, maintenance records, and compliance documentation.',
      icon: 'Wrench',
      status: 'inactive',
      isPrimary: false,
      locations: 0,
      lastUpdated: 'N/A',
      route: null
    },
    {
      id: 'emergency-response',
      name: 'Emergency Response',
      description: 'Emergency preparedness planning, drill management, and real-time incident response coordination tools.',
      icon: 'Siren',
      status: 'inactive',
      isPrimary: false,
      locations: 0,
      lastUpdated: 'N/A',
      route: null
    }
  ];

  const subscriptionMetrics = {
    activeModules: 6,
    totalModules: 10,
    moduleUsage: 60,
    renewalDate: '06/15/2026',
    supportLevel: '24/7 Priority'
  };

  const locations = [
    { value: 'plant-1', label: 'Plant 1 - Main Facility', activeModules: 2 },
    { value: 'plant-2', label: 'Plant 2 - North Wing', activeModules: 2 },
    { value: 'plant-3', label: 'Plant 3 - South Wing', activeModules: 2 },
    { value: 'warehouse-1', label: 'Warehouse 1', activeModules: 2 }
  ];

  const activeModules = modules?.filter(m => m?.status === 'active');

  const handleActivateModule = (module) => {
    console.log('Activating module:', module.name);
  };

  const handleConfigureModule = (module) => {
    console.log('Configuring module:', module.name);
  };

  const handleAccessModule = (module) => {
    if (module?.route) {
      navigate(module.route);
    }
  };

  const handleViewDetails = (module) => {
    if (module?.route) {
      navigate(module.route);
    }
  };

  const handleCreatePermit = () => {
    navigate('/work-permit-creation');
  };

  const handleViewAnalytics = () => {
    navigate('/safety-analytics-dashboard');
  };

  const handleManageUsers = () => {
    console.log('Manage users clicked');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-foreground">Module Selection Dashboard</h1>
          <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
            <Icon name="Shield" size={20} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">System Active</span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Select and manage your safety modules. Configure modules based on your facility needs and subscription plan.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-6">
          <QuickActions
            userRole={userRole}
            onCreatePermit={handleCreatePermit}
            onViewAnalytics={handleViewAnalytics}
            onManageUsers={handleManageUsers}
          />

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Available Modules</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {modules?.map((module) => (
                <ModuleCard
                  key={module?.id}
                  module={module}
                  onActivate={handleActivateModule}
                  onConfigure={handleConfigureModule}
                  onAccess={handleAccessModule}
                  userRole={userRole}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <SubscriptionSummary subscription={subscriptionMetrics} />
          <LocationFilter
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            locations={locations}
            showAllLocations={true}
          />
          <ActiveModulesList
            modules={activeModules}
            onViewDetails={handleViewDetails}
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Need Help Getting Started?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Our support team is available 24/7 to help you configure and activate modules. Contact us for personalized onboarding assistance.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                <Icon name="Phone" size={16} />
                Contact Support
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
                <Icon name="BookOpen" size={16} />
                View Documentation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleSelectionDashboard;